import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import {
  ImagingOrderCreatePayload,
  ImagingOrderRes,
  ImagingOrderResultCreatePayload,
  ImagingOrderStatus,
  ImagingOrderUpdatePayload,
  SingleImagingOrderRes
} from '../types/imaging_order.type'
import type { ImagingResultCreateFromTemplatePayload } from '../types/imaging_results.type'

type ImagingOrderResultPayload = ImagingOrderResultCreatePayload | ImagingResultCreateFromTemplatePayload

type ImagingOrderTypes = {
  created_at?: string
  encounterId?: string
  id?: string
  limit?: string
  page?: string
  patientId?: string
  query?: string
  sortDir?: string
  status?: ImagingOrderStatus
  /** `queue=imaging`: only orders with something for our department to do (outside-only orders are hidden). */
  queue?: 'imaging'
}

const JSON_API = { headers: { 'Content-Type': 'application/vnd.api+json' } }

/** Query string of `GET /imaging_orders` for the given filters (exported for tests). */
export const buildImagingOrderListParams = (arg?: ImagingOrderTypes): Record<string, string> => {
  const { created_at, encounterId, limit, page, patientId, query, queue, sortDir, status } = arg ?? {}
  return {
    ...(patientId && { patient_id: patientId }),
    ...(encounterId && { encounter_id: encounterId }),
    ...(status && { status }),
    ...(sortDir && { sort_dir: sortDir }),
    ...(query && { query }),
    ...(limit && { 'page[size]': limit }),
    ...(page && { 'page[number]': page }),
    ...(created_at && { created_at }),
    ...(queue && { queue })
  }
}

/**
 * `/imaging_orders` — bundle resource where one order carries N studies, mixing
 * catalog picks (`imaging_ids`) with free-text names (`study_names`).
 *
 * Pass `arg` to load a list/detail. Call with no args when you only need mutations
 * (avoids a competing unfiltered list fetch that can race the real list).
 */
export const useImagingOrder = (arg?: ImagingOrderTypes) => {
  const { created_at, encounterId, id, limit, page, patientId, query, queue, sortDir, status } = arg ?? {}
  const wantsList = arg != null && !id

  const { data: imagingOrders, isLoading } = useQuery({
    enabled: wantsList,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<ImagingOrderRes>('/imaging_orders', {
        params: buildImagingOrderListParams(arg)
      })
      return data
    },
    queryKey: ['imaging_order', { created_at, encounterId, limit, page, patientId, query, queue, sortDir, status }]
  })

  const { data: singleImagingOrder, isLoading: singleImagingOrderIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<SingleImagingOrderRes>(`/imaging_orders/${id}`)
      return data
    },
    queryKey: ['imaging_order', { id }]
  })

  /** Invalidate + await refetch so lists update before modals close. */
  const invalidate = async (orderId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['imaging_order'] }),
      orderId ? queryClient.invalidateQueries({ queryKey: ['imaging_order', { id: orderId }] }) : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: ['orders'] }),
      queryClient.invalidateQueries({ queryKey: ['imaging_result'] })
    ])
  }

  const { mutateAsync: createImagingOrder } = useMutation({
    mutationFn: async (payload: ImagingOrderCreatePayload) => {
      const { data } = await AXIOS_INSTANCE.post('/imaging_orders', payload, JSON_API)
      return data
    },
    onSuccess: async () => {
      await invalidate()
    }
  })

  /** Only valid while `study_names_editable` — i.e. before any file has been uploaded. */
  const { mutateAsync: updateImagingOrder } = useMutation({
    mutationFn: async ({ formVal, id: orderId }: { formVal: ImagingOrderUpdatePayload; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/imaging_orders/${orderId}`, formVal, JSON_API)
      return data
    },
    onSuccess: async (_data, { id: orderId }) => {
      await invalidate(orderId)
    }
  })

  const { mutateAsync: completeImagingOrder } = useMutation({
    mutationFn: async ({ id: orderId }: { id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(
        `imaging_orders/${orderId}/complete`,
        { data: { type: 'imaging_order' } },
        JSON_API
      )
      return data
    },
    onSuccess: async (_data, { id: orderId }) => {
      await invalidate(orderId)
    }
  })

  const { mutateAsync: cancelImagingOrder } = useMutation({
    mutationFn: async ({ cancellation_reason, id: orderId }: { cancellation_reason: string; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(
        `imaging_orders/${orderId}/cancel`,
        { data: { type: 'imaging_order', attributes: { cancellation_reason } } },
        JSON_API
      )
      return data
    },
    onSuccess: async (_data, { id: orderId }) => {
      await invalidate(orderId)
    }
  })

  /**
   * Attach a file or structured template result to the bundle
   * (`POST /imaging_orders/:id/imaging_results`).
   */
  const { mutateAsync: createImagingOrderResult } = useMutation({
    mutationFn: async ({
      formVal,
      imaging_order_id
    }: {
      formVal: ImagingOrderResultPayload
      imaging_order_id: string
    }) => {
      if (!imaging_order_id) throw new Error('imaging_order_id is required')
      const { data } = await AXIOS_INSTANCE.post(
        `/imaging_orders/${imaging_order_id}/imaging_results`,
        formVal,
        JSON_API
      )
      return data
    },
    onSuccess: async (_data, { imaging_order_id }) => {
      await invalidate(imaging_order_id)
    }
  })

  return {
    cancelImagingOrder,
    completeImagingOrder,
    createImagingOrder,
    createImagingOrderResult,
    imaging_orders: imagingOrders?.data ?? [],
    imagingOrders,
    included: imagingOrders?.included,
    isLoading: wantsList ? isLoading : false,
    singleImagingOrder,
    singleImagingOrderIsLoading,
    updateImagingOrder
  }
}
